import { useEffect, useRef, useState } from 'react';
import { PlayCircle, Video, X } from 'lucide-react';

const VIDEO_SRC = '/RESQLY_Ranger_Tutorial_FINAL_POLISHED.mp4';

export default function AuthTutorialLink() {
  const [open, setOpen] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const closeButtonRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const onNativeBack = () => {
      setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resqly:ranger-tutorial-back', onNativeBack);
    document.body.dataset.rangerTutorialOpen = '1';

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const frameId = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();

      const video = videoRef.current;
      if (!video) return;

      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          // Browser autoplay policy can block playback; native controls stay available.
        });
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resqly:ranger-tutorial-back', onNativeBack);
      delete document.body.dataset.rangerTutorialOpen;
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setVideoError(false);
  };

  const openTutorial = () => {
    setVideoError(false);
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        data-testid="ranger-tutorial-link"
        onClick={openTutorial}
        className="mx-auto flex items-center justify-center gap-2 py-2 text-xs font-semibold text-blue-700 transition-opacity hover:opacity-80"
        aria-label="Tutorial to join as a Ranger"
      >
        <PlayCircle className="h-4 w-4" aria-hidden="true" />
        <span>Tutorial to join as a Ranger</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-sm"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="ranger-tutorial-title"
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-black shadow-2xl"
          >
            <div className="flex items-center justify-between gap-3 bg-white px-4 py-3">
              <div className="min-w-0">
                <p id="ranger-tutorial-title" className="truncate text-sm font-semibold text-slate-900">
                  Tutorial to join as a Ranger
                </p>
                <p className="text-[11px] text-slate-500">Watch before creating your Ranger account.</p>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={close}
                aria-label="Close Ranger tutorial"
                className="shrink-0 rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            {videoError ? (
              <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 bg-slate-950 px-6 py-12 text-center text-white">
                <Video className="h-8 w-8 text-slate-400" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold">Tutorial video is not available yet.</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Please try again after the Ranger tutorial video has been uploaded.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setVideoError(false)}
                  className="rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10"
                >
                  Retry
                </button>
              </div>
            ) : (
              <video
                ref={videoRef}
                data-testid="ranger-tutorial-video"
                className="block max-h-[75vh] w-full bg-black"
                controls
                autoPlay
                playsInline
                preload="auto"
                onError={() => setVideoError(true)}
                onCanPlay={() => {
                  const playPromise = videoRef.current?.play();
                  if (playPromise?.catch) {
                    playPromise.catch(() => {
                      // Keep controls available when autoplay is blocked by the browser.
                    });
                  }
                }}
              >
                <source src={VIDEO_SRC} type="video/mp4" />
                Your browser does not support HTML5 video.
              </video>
            )}
          </section>
        </div>
      )}
    </>
  );
}
